export interface FieldValidationRule {
  kind: 'TEXT' | 'NUMERIC' | 'DECIMAL' | 'DATE' | 'URL';
  nullable?: boolean;
  integer?: boolean;
  year?: boolean;
  min?: number;
  max?: number;
  occupancyStatusText?: boolean;
}

export interface SourceValidationErrorLike {
  rowNumber: number;
  value: string;
  expected: string;
  message?: string;
}

/** Converts backend "expected" hints into user-facing English labels. */
export function humanizeValidationExpected(expected: string): string {
  const trimmed = expected.trim();
  if (!trimmed) return 'a valid value';

  if (trimmed.startsWith('one of:')) {
    const values = trimmed
      .slice('one of:'.length)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (values.length <= 4) {
      return `one of: ${values.join(', ')}`;
    }
    return `one of ${values.length} allowed values`;
  }

  if (/^one of \d+ allowed values$/i.test(trimmed)) {
    return trimmed;
  }

  const labels: Record<string, string> = {
    number: 'a number',
    integer: 'an integer',
    'four-digit year': 'a four-digit year (YYYY)',
    'date in YYYY-MM-DD format': 'a date (YYYY-MM-DD)',
    URL: 'a valid URL',
    text: 'text',
  };
  if (labels[trimmed]) return labels[trimmed];

  if (trimmed.startsWith('decimal')) {
    return trimmed
      .replace(/^decimal/, 'a decimal')
      .replace(/between -∞ and /, 'between ')
      .replace(/ and ∞$/, ' or less')
      .replace(/-∞ and /, 'no minimum and ');
  }

  if (trimmed.startsWith('a ') || trimmed.startsWith('an ')) {
    return trimmed;
  }

  return `a valid ${trimmed}`;
}

function isOccupancyStatusText(value: string): boolean {
  const words = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!words || !/[a-z]/.test(words)) return false;
  if (/\b(not|non)\s+occupied\b/.test(words)) return true;
  if (/\bnot\s+vacant\b/.test(words)) return true;
  const hasVacant = /\b(vacant|vacancy|unoccupied|empty|available)\b/.test(words);
  const hasOccupied = /\b(occupied|occupancy|leased|tenanted|inhabited)\b/.test(words);
  return (hasVacant && !hasOccupied) || (hasOccupied && !hasVacant);
}

export function formatSourceValidationRowError(error: SourceValidationErrorLike): string {
  if (error.message?.trim()) {
    return `Row ${error.rowNumber}: ${error.message.trim()}`;
  }

  const isBlank = !error.value || error.value === '(blank)';
  const valueLabel = isBlank ? 'A blank value' : `"${error.value}"`;
  return `Row ${error.rowNumber}: ${valueLabel} is invalid. Expected ${humanizeValidationExpected(error.expected)}.`;
}

export function validateFieldValue(
  value: string,
  allowedValues: string[],
  rule?: FieldValidationRule
): string | null {
  const text = value.trim();
  if (!text) {
    return rule?.nullable === false ? 'This field is required.' : null;
  }

  if (allowedValues.length > 0) {
    return allowedValues.includes(text)
      ? null
      : `Select one of the allowed values.`;
  }

  if (!rule) return null;

  if (rule.kind === 'URL') {
    try {
      // eslint-disable-next-line no-new
      new URL(text);
      return null;
    } catch {
      return 'Enter a valid URL.';
    }
  }

  if (rule.kind === 'DATE') {
    const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!ymd) return 'Enter a date in YYYY-MM-DD format.';
    const y = Number.parseInt(ymd[1], 10);
    const m = Number.parseInt(ymd[2], 10);
    const d = Number.parseInt(ymd[3], 10);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
      ? null
      : 'Enter a valid date.';
  }

  if (rule.kind === 'NUMERIC' || rule.kind === 'DECIMAL') {
    if (rule.occupancyStatusText && isOccupancyStatusText(text)) {
      return null;
    }

    const normalized = text.replace(/[$,]/g, '').replace(/\s+/g, '');
    if (
      !/^[+-]?\d*(?:\.\d+)?%?$/.test(normalized) ||
      normalized === '' ||
      normalized === '.' ||
      normalized === '%'
    ) {
      if (rule.integer) return 'Enter a valid integer.';
      if (rule.kind === 'DECIMAL') return 'Enter a valid decimal.';
      return 'Enter a valid number.';
    }
    const hasPercent = normalized.endsWith('%');
    let numericValue = Number.parseFloat(hasPercent ? normalized.slice(0, -1) : normalized);
    if (!Number.isFinite(numericValue)) return 'Enter a valid number.';
    if (hasPercent && rule.kind === 'DECIMAL') numericValue = numericValue / 100;
    if (rule.integer && !Number.isInteger(numericValue)) return 'Enter an integer.';
    if (rule.year && (numericValue < 1000 || numericValue > 9999)) return 'Enter a four-digit year.';
    if (rule.min !== undefined && numericValue < rule.min) {
      return `Enter a value greater than or equal to ${rule.min}.`;
    }
    if (rule.max !== undefined && numericValue > rule.max) {
      return `Enter a value less than or equal to ${rule.max}.`;
    }
  }

  return null;
}
